import { useEffect, useRef, useState } from "react";
import { http, downloadUrl } from "../lib/api";
import { FileText, ChevronDown, Eye } from "lucide-react";
import PdfPreviewPanel from "./fin/PdfPreviewPanel";

const dateOf = (d) => {
  const src = /^\d{4}-\d{2}-\d{2}/.test(d.note || "") ? d.note : d.created_at;
  const t = new Date(src);
  return isNaN(t) ? "" : `${String(t.getDate()).padStart(2, "0")}/${String(t.getMonth() + 1).padStart(2, "0")}/${t.getFullYear()}`;
};
const hrefOf = (d) => downloadUrl(`/personnel/documents/${d.id}/download`);
// panelde gösterilebilen türler (pdf / png / jpg); mhtml-html yeni sekmede açılır
const previewable = (d) => ["application/pdf", "image/png", "image/jpeg"].includes(d.content_type) || /\.(pdf|png|jpe?g)$/i.test(d.original_filename || "");
const openDoc = (d) => { const a = document.createElement("a"); a.href = hrefOf(d); a.target = "_blank"; a.rel = "noreferrer"; document.body.appendChild(a); a.click(); a.remove(); };
const btnCls = (count, kind) => count
  ? (kind === "hire" ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100" : "bg-red-50 border-red-300 text-red-800 hover:bg-red-100")
  : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed";

// Dışarı tıklanınca kapanan basit açılır liste davranışı
function useOutsideClose(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, setOpen]);
  return ref;
}

function DocList({ docs, kind, label, onPick }) {
  return (
    <div className="absolute right-0 z-30 mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-xl py-1" data-testid={`bildirge-${kind}-list`}>
      {docs.map((d) => (
        <button type="button" key={d.id} onClick={() => onPick(d)} data-testid={`bildirge-${kind}-item-${d.id}`} className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50 cursor-pointer">
          <Eye className="w-3 h-3 text-slate-400 shrink-0" /><span className="font-semibold">{dateOf(d)} tarihli {label}</span><span className="ml-auto text-[10px] text-slate-400 truncate max-w-[90px]" title={d.original_filename}>{d.original_filename}</span>
        </button>
      ))}
    </div>
  );
}

// İşe Giriş / İşten Çıkış tarihinin yanındaki PDF butonu: tek belge → panelde aç; birden çok → tarihli liste (seçilen panelde açılır; mhtml yeni sekmede)
export default function BildirgeButton({ personId, kind, refreshKey }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const ref = useOutsideClose(open, setOpen);
  const label = kind === "hire" ? "İşe Giriş" : "İşten Çıkış";
  useEffect(() => {
    if (!personId) return;
    http.get(`/personnel/${personId}/documents`).then((r) => setDocs(r.data.filter((d) => d.kind === kind && d.content_type !== "multipart/related"))).catch(() => {});
  }, [personId, kind, refreshKey]);
  if (!personId) return null;
  const show = (d) => { setOpen(false); if (previewable(d)) setPreview({ path: `/personnel/documents/${d.id}/download`, name: d.original_filename, title: `${dateOf(d)} tarihli ${label} Bildirgesi` }); else openDoc(d); };
  const activate = () => {
    if (!docs.length) return;
    if (docs.length === 1) show(docs[0]); else setOpen((o) => !o);
  };
  return (
    <div ref={ref} className="relative shrink-0" data-testid={`bildirge-${kind}`}>
      <div role="button" tabIndex={docs.length ? 0 : -1} aria-disabled={!docs.length} onClick={activate} onKeyDown={(e) => { if (e.key === "Enter") activate(); }} data-testid={`bildirge-${kind}-btn`}
        title={docs.length ? `${docs.length} ${label} bildirgesi — panelde görüntüle` : `${label} bildirgesi yok`}
        className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-semibold border whitespace-nowrap ${btnCls(docs.length, kind)}`}>
        <FileText className="w-3.5 h-3.5" /> {label} PDF{docs.length > 1 && <><span className="px-1 rounded bg-white/80 font-bold">{docs.length}</span><ChevronDown className="w-3 h-3" /></>}
      </div>
      {open && docs.length > 1 && <DocList docs={docs} kind={kind} label={label} onPick={show} />}
      <PdfPreviewPanel doc={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
